import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	DropdownItemRadio,
} from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import ThemeIcon from '@atlaskit/icon/core/theme';
import { ButtonItem } from '@atlaskit/menu';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { type ThemeColorModes } from '@atlaskit/tokens';

import type { AccountMenuProps } from '../account-menu';

import DarkModeThumbnail from './thumbnails/dark-mode.svg';
import LightModeThumbnail from './thumbnails/light-mode.svg';
import MatchBrowserThumbnail from './thumbnails/match-browser.svg';

type ThemeOptionType = {
	label: JSX.Element;
	thumbnail: string;
	value: ThemeColorModes;
};

type ThemeSwitcherProps = Required<Pick<AccountMenuProps, 'onThemeChange' | 'currentTheme'>> &
	Pick<AccountMenuProps, 'testId' | 'globalColorMode' | 'onThemeSwitcherOpenChange'> & { appearancePreferencesLink?: string };

export const ThemeSwitcher = ({
	testId,
	onThemeChange,
	currentTheme,
	globalColorMode,
	appearancePreferencesLink,
	onThemeSwitcherOpenChange,
}: ThemeSwitcherProps): React.JSX.Element => {
	return (
		<>
			{themeOptions.map(({ value, thumbnail }) => (
				<link rel="preload" href={thumbnail} as="image" key={value} />
			))}

			<DropdownMenu
				testId={testId}
				placement="auto-start"
				onOpenChange={onThemeSwitcherOpenChange}
				trigger={({ triggerRef, ...props }) => (
					<ButtonItem
						ref={triggerRef}
						onClick={(event) => event.stopPropagation()}
						iconBefore={<ThemeIcon color="currentColor" label="" />}
						iconAfter={<ChevronRightIcon color="currentColor" label="" size="small" />}
						{...props}
					>
						<FormattedMessage
							id="identity.account-menu.theme-switcher.theme-button"
							defaultMessage="Theme"
							description="Label for button in the account menu to switch to a different theme"
						/>
					</ButtonItem>
				)}
				shouldRenderToParent={false}
			>
				{themeOptions.map(({ label, value, thumbnail }) => {
					const isSynced = globalColorMode !== undefined && globalColorMode === value;
					const isSelected = currentTheme === value;

					return (
						<DropdownItemRadio
							testId={`${testId}--radio-${value}`}
							id={value}
							key={value}
							onClick={() => onThemeChange(value)}
							isSelected={isSelected}
						>
							<Inline space="space.150" alignBlock="center">
								<img src={thumbnail} width={64} height={48} alt="" />
								<Stack space="space.025">
									{label}
									{isSynced && (
										<Text size="small" color="color.text.subtlest">
											<FormattedMessage {...messages.syncedAcrossApps} />
										</Text>
									)}
								</Stack>
							</Inline>
						</DropdownItemRadio>
					);
				})}

				{appearancePreferencesLink && (
					<DropdownItemGroup hasSeparator>
						<DropdownItem
							href={appearancePreferencesLink}
							target="_blank"
							rel="noopener"
							testId={testId && `${testId}--personalize`}
							elemAfter={<LinkExternalIcon color="currentColor" label="" size="small" />}
						>
							<FormattedMessage {...messages.personalize} />
						</DropdownItem>
					</DropdownItemGroup>
				)}
			</DropdownMenu>
		</>
	);
};

const messages = defineMessages({
	light: {
		id: 'identity.account-menu.theme-switcher.light.option',
		defaultMessage: 'Light',
		description: 'Label for the theme switcher light option',
	},
	dark: {
		id: 'identity.account-menu.theme-switcher.dark.option',
		defaultMessage: 'Dark',
		description: 'Label for the theme switcher dark option',
	},
	matchBrowser: {
		id: 'identity.account-menu.theme-switcher.match-browser.option',
		defaultMessage: 'Match browser',
		description: 'Label for the theme switcher match browser option',
	},
	syncedAcrossApps: {
		id: 'identity.account-menu.theme-switcher.synced-across-apps',
		defaultMessage: 'Synced across apps',
		description:
			'Indicates the theme option is synced with global account settings across all apps',
	},
	personalize: {
		id: 'identity.account-menu.theme-switcher.personalize',
		defaultMessage: 'Personalize',
		description: 'Link to personalize settings in the theme switcher menu',
	},
});

const themeOptions: readonly ThemeOptionType[] = [
	{
		label: <FormattedMessage {...messages.light} />,
		value: 'light',
		thumbnail: LightModeThumbnail,
	},
	{
		label: <FormattedMessage {...messages.dark} />,
		value: 'dark',
		thumbnail: DarkModeThumbnail,
	},
	{
		label: <FormattedMessage {...messages.matchBrowser} />,
		value: 'auto',
		thumbnail: MatchBrowserThumbnail,
	},
] as const;
